import { useAskHelenaConversationMessages } from "controller";
import { useSetAtom } from "jotai";
import { useEffect } from "react";
import { getApiErrorMessage } from "utilz/helpers";
import { loadAskHelenaConversation } from "../store";
import { toAskHelenaMessage } from "../utils/toAskHelenaMessage";

interface UseAskHelenaConversationHistoryReturn {
  isLoading: boolean;
  /** Null unless the fetch failed — a 404 included, see below. */
  errorMessage: string | null;
}

/**
 * Loads a conversation picked from the sidebar and hands it to the Ask Helena
 * state machine, so the thread renders and the next question continues it
 * instead of starting a new conversation.
 *
 * A 404 is NOT reported as "deleted": the service answers 404 both for an
 * unknown id and for a colleague's thread, deliberately, and the UI must not
 * invent a distinction the API refuses to make.
 */
export const useAskHelenaConversationHistory = (
  conversationId: string | null,
): UseAskHelenaConversationHistoryReturn => {
  const loadConversation = useSetAtom(loadAskHelenaConversation);

  const { data, isFetching, isError, error } = useAskHelenaConversationMessages(
    {
      conversationId: conversationId ?? "",
      enabled: !!conversationId,
    },
  );

  useEffect(() => {
    if (!(conversationId && data)) return;

    loadConversation({
      conversationId,
      messages: data.map(toAskHelenaMessage),
    });
  }, [conversationId, data, loadConversation]);

  return {
    isLoading: !!conversationId && isFetching && !data,
    errorMessage: isError
      ? getApiErrorMessage(error, "This conversation couldn't be opened.")
      : null,
  };
};
